import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const TABS = [
  { key: 'payments', label: 'Payments' },
  { key: 'webhooks', label: 'Webhooks' },
  { key: 'reconciliation', label: 'Reconciliation' },
];

const MATCH_COLORS = {
  matched: 'text-emerald-700 bg-emerald-50',
  amount_mismatch: 'text-rose-700 bg-rose-50',
  missing_from_razorpay: 'text-amber-700 bg-amber-50',
};

export default function AdminPaymentOperations() {
  const [tab, setTab] = useState('payments');
  const [payments, setPayments] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [reconRows, setReconRows] = useState([]);
  const [reconSummary, setReconSummary] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('');

  const loadPayments = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (paymentTypeFilter) params.set('payment_type', paymentTypeFilter);
    api.get(`/admin/payments?${params.toString()}`)
      .then(({ data }) => setPayments(data.payments || []))
      .catch(() => toast.error('Could not load payments'))
      .finally(() => setLoading(false));
  };

  const loadWebhooks = () => {
    setLoading(true);
    api.get('/admin/payment-webhooks')
      .then(({ data }) => setWebhooks(data.events || []))
      .catch(() => toast.error('Could not load webhooks'))
      .finally(() => setLoading(false));
  };

  const loadReconciliation = () => {
    setLoading(true);
    api.get('/admin/payment-reconciliation')
      .then(({ data }) => {
        setReconRows(data.rows || []);
        setReconSummary(data.summary || null);
      })
      .catch(() => toast.error('Could not load reconciliation'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (tab === 'payments') loadPayments();
    if (tab === 'webhooks') loadWebhooks();
    if (tab === 'reconciliation') loadReconciliation();
  }, [tab, statusFilter, paymentTypeFilter]);

  const openPayment = async (id) => {
    try {
      const { data } = await api.get(`/admin/payments/${id}`);
      setSelectedPayment(data.payment);
    } catch {
      toast.error('Could not load payment detail');
    }
  };

  const replayWebhook = async (eventId) => {
    try {
      await api.post(`/admin/payment-webhooks/${eventId}/replay`);
      toast.success('Webhook replayed');
      loadWebhooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Replay failed');
    }
  };

  return (
    <div className="resqly-shell">
      <div className="resqly-frame">
        <MobileShell title="Payment Operations" header>
          <div className="px-5 py-5 space-y-4" data-testid="admin-payment-ops">
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  data-testid={`admin-payment-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`px-3 py-1.5 rounded-full text-sm border whitespace-nowrap ${
                    tab === t.key ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-slate-700 border-slate-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {loading && <p className="text-sm text-slate-500">Loading…</p>}

            {tab === 'payments' && (
              <div className="space-y-3">
                <div className="flex gap-2 flex-wrap">
                  <select
                    className="border rounded px-2 py-1 text-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="admin-payment-status-filter"
                  >
                    <option value="">All statuses</option>
                    <option value="paid">paid</option>
                    <option value="order_created">order_created</option>
                    <option value="failed">failed</option>
                  </select>
                  <select
                    className="border rounded px-2 py-1 text-sm"
                    value={paymentTypeFilter}
                    onChange={(e) => setPaymentTypeFilter(e.target.value)}
                  >
                    <option value="">All types</option>
                    <option value="base_booking">base_booking</option>
                    <option value="extra_service">extra_service</option>
                    <option value="provider_recharge">provider_recharge</option>
                  </select>
                </div>
                {payments.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="resqly-card p-3 text-sm w-full text-left hover:shadow-md"
                    data-testid="admin-payment-row"
                    onClick={() => openPayment(p.id)}
                  >
                    <div className="font-medium">₹{p.amount} — {p.payment_type}</div>
                    <div className="text-slate-500">{p.status} / {p.gateway_status || '—'}</div>
                    <div className="text-xs text-slate-400 truncate">{p.razorpay_order_id}</div>
                  </button>
                ))}
                {selectedPayment && (
                  <div className="resqly-card p-4 text-sm space-y-1" data-testid="admin-payment-detail">
                    <div className="font-semibold">Payment detail</div>
                    <div>ID: {selectedPayment.id}</div>
                    <div>Booking: {selectedPayment.booking_id || '—'}</div>
                    <div>Consumer: {selectedPayment.consumer_id || '—'}</div>
                    <div>Provider: {selectedPayment.provider_id || '—'}</div>
                    <div>Order: {selectedPayment.razorpay_order_id}</div>
                    <div>Refund: {selectedPayment.refund_status || '—'}</div>
                  </div>
                )}
              </div>
            )}

            {tab === 'webhooks' && (
              <div className="space-y-3">
                {webhooks.map((e) => (
                  <div key={e.event_id} className="resqly-card p-3 text-sm" data-testid="admin-webhook-row">
                    <div className="font-medium">{e.event_type}</div>
                    <div className="text-slate-500">{e.received_at}</div>
                    <div>Processed: {e.processed ? 'yes' : 'no'}</div>
                    {e.error_reason && <div className="text-rose-600">{e.error_reason}</div>}
                    {e.has_payload && (
                      <Button size="sm" className="mt-2" onClick={() => replayWebhook(e.event_id)}>
                        Replay
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {tab === 'reconciliation' && (
              <div className="space-y-3">
                {reconSummary && (
                  <div className="resqly-card p-3 text-sm grid grid-cols-3 gap-2" data-testid="recon-summary">
                    <div>Matched: {reconSummary.matched}</div>
                    <div>Mismatch: {reconSummary.amount_mismatch}</div>
                    <div>Missing RZP: {reconSummary.missing_from_razorpay}</div>
                  </div>
                )}
                {reconRows.map((row) => (
                  <div key={row.payment_id} className="resqly-card p-3 text-sm" data-testid="recon-row">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${MATCH_COLORS[row.match_status] || ''}`}>
                      {row.match_status}
                    </span>
                    <div className="mt-1">Payment {row.payment_id}</div>
                    <div className="text-slate-500">
                      RESQLY {row.resqly?.amount_paise} paise — RZP {row.razorpay?.amount_paise ?? '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Link to="/admin/dashboard" className="block text-center text-sm text-blue-700 font-semibold py-2">
              Back to Admin Console
            </Link>
          </div>
        </MobileShell>
      </div>
    </div>
  );
}
